import { Divider, Edit, Form, Input, SaveButton, Select, Tag, useFileUploadState, useForm, useSelect } from "@pankod/refine-antd";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { RenderParagraphWithImage } from "components/common/renderParagraphWithImage";
import { CancelButton } from "components/utils/cancelButton";
import { ILookup } from "interfaces";
import { ITechJam } from "interfaces/article";
import { useEffect } from "react";
import { DATAPROVIDER_LOOKUP, DATAPROVIDER_UPDATE, RESOURCE_PATH } from "scripts/site";

const DEFAULT_TAG = "Tech Jam";

export const TechJamEdit: React.FC<IResourceComponentsProps> = () => {

  const { formProps, saveButtonProps, queryResult, form } = useForm<ITechJam>({
    dataProviderName: DATAPROVIDER_UPDATE,
    redirect: "show",
  });

  const handleFinish = (values: any) => {
    // Ensure Tech Jam tag is always included
    const tags = values.tagList || [];
    if (!tags.includes(DEFAULT_TAG)) {
      tags.unshift(DEFAULT_TAG);
    }
    // Call the original onFinish from formProps
    return formProps.onFinish?.({ ...values, tagList: tags });
  };

  const { isLoading: isUploading, onChange: onUploadChange } = useFileUploadState();
  const record = queryResult?.data?.data;

  // Ensure Tech Jam tag is present when editing
  useEffect(() => {
    if (record && form) {
      const currentTags = form.getFieldValue("tagList") || [];
      if (!currentTags.includes(DEFAULT_TAG)) {
        form.setFieldsValue({ tagList: [DEFAULT_TAG, ...currentTags] });
      }
    }
  }, [record, form]);

  const { selectProps: usersList } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.PEOPLE,
    optionLabel: "nameEmail",
    optionValue: "id",
    filters: [{
      field: "stateManager.state",
      operator: "eq",
      value: "active"
    }],
    sort: [{
      field: "lastAccessedAt",
      order: "desc"
    }]
  });

  return (
    <Edit
      isLoading={queryResult?.isFetching}
      title={`Update - ${queryResult?.isFetching ? "" : record?.name}`}
      headerButtons={() => (<></>)}
      footerButtons={() => (
        queryResult?.isFetching ? "" :
          <>
            <SaveButton {...saveButtonProps} disabled={isUploading} />
            <CancelButton />
          </>
      )}>
      <Form {...formProps} layout="vertical" autoComplete="off" onFinish={handleFinish}>
        <Form.Item
          label="Title"
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              min: 5,
              max: 100
            },
          ]}
        >
          <Input placeholder="Session title" />
        </Form.Item>
        <Form.Item
          label="Short description"
          name="description"
          rules={[
            {
              required: true,
              whitespace: true,
              min: 15,
              max: 200
            },
          ]}
        >
          <Input.TextArea rows={2} placeholder="Brief summary covering audience, learning goals, and highlights" />
        </Form.Item>

        <Form.Item
          label="Tags"
          name="tagList"
          tooltip="Add tags. 'Tech Jam' tag is mandatory and cannot be removed."
          rules={[{
            required: true,
            type: "array"
          }]}
        >
          <Select mode="tags"
            placeholder="Type a keyword and press enter. You can add as many as you want."
            tokenSeparators={[',']}
            onChange={(value: string[]) => {
              // Remove duplicate "Tech Jam" tags and ensure only one exists at the start
              const filtered = value.filter(tag => tag !== DEFAULT_TAG);
              const updatedTags = [DEFAULT_TAG, ...filtered];
              form?.setFieldsValue({ tagList: updatedTags });
            }}
            tagRender={(props) => {
              const { label, closable, onClose } = props;
              const isDefaultTag = (label === "Tech Jam");
              return (
                <Tag
                  color={isDefaultTag ? "blue" : "default"}
                  closable={!isDefaultTag && closable}
                  onClose={onClose}
                >{label}</Tag>
              );
            }}
          >
          </Select>
        </Form.Item>
        <Form.Item
          label="Speaker(s)"
          name="authorName"
          rules={[{
            required: true,
            whitespace: true,
            type: "array",
            min: 1,
            max: 3,
            message: "Minimum 1 and maximum of 3 speakers"
          }]}
        >
          <Select placeholder="Search Name or Email" mode="multiple" {...usersList} allowClear />
        </Form.Item>

        <Divider orientation="left">Session Content</Divider>
        <Form.Item
          label="Artifact URL"
          name="artifactUrl"
          tooltip="OneDrive shared folder link containing the session presentation and recording"
          rules={[
            {
              required: true,
              message: "Please provide the artifact URL"
            },
            {
              type: "url",
              message: "Please enter a valid URL"
            }
          ]}
          extra={
            <span style={{ color: "#faad14" }}>
              ⚠️ <strong>Important:</strong> Do <strong>not</strong> share the folder with everyone in the organization.
              Users should request access, which will then be approved by the session host.
            </span>
          }
        >
          <Input placeholder="https://microsoft-my.sharepoint.com/..." />
        </Form.Item>
        <RenderParagraphWithImage
          resourcePath={RESOURCE_PATH.TECHJAM} onUploadChange={onUploadChange}
          images={record?.articleImages}
          paragraphFieldName={"body"} imagesFieldName={"articleImages"} featuredImage={true} />
      </Form>
    </Edit>
  );
};